import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { createToonGradientMap, toonMat } from './materials.js';
import type { WeaponKind } from './WeaponSystem.js';
import { rollRarity, type WeaponRarity } from './FpsWeaponRarity.js';
import { rollModsFromChest, type WeaponMod } from './FpsWeaponMods.js';

const INTERACT_RANGE = 3;
const LOOK_DOT_MIN = 0.88;
const RARE_CHEST_CHANCE = 0.2;

const BEAM_RADIUS = 0.06;
const BEAM_HEIGHT = 11;
const BEAM_OPACITY_BASE = 0.22;
const BEAM_PULSE_SPEED = 2.4;
const BEAM_PULSE_AMP = 0.08;
const BEAM_GOLD = 0xf1c40f;
const BEAM_PURPLE = 0xb44dff;
const AURA_BLUE = 0x4488ff;
const AURA_PURPLE = 0xb44dff;
const AURA_GOLD = 0xf1c40f;
const AURA_PULSE_SPEED = 2.1;
const AURA_PULSE_AMP = 0.22;

const OPEN_ANIM_DURATION = 0.35;
const LID_OPEN_ANGLE = -Math.PI * 0.62;
const ANTICIPATION_RING_OPACITY = 0.24;
const ANTICIPATION_GLOW_OPACITY = 0.14;
const ANTICIPATION_PROMPT_OPACITY = 0.5;

export type ChestTier = 'normal' | 'rare';
export type ChestConsumable = 'bandage' | 'mini' | 'chug';

export type ChestWeaponDrop = {
  kind: WeaponKind;
  rarity: WeaponRarity;
};

export const CHEST_LOOT_STAGGER_SEC = 0.14;

export function chestLootRarityFlashHex(
  rarity: WeaponRarity | undefined,
  tier: ChestTier,
): number {
  if (rarity === 'legendary') return 0xffd700;
  if (rarity === 'epic') return 0xb44dff;
  if (rarity === 'rare') return 0x4488ff;
  if (rarity === 'uncommon') return 0x58d68d;
  if (tier === 'rare') return 0xb44dff;
  return 0xffd54a;
}

export type ChestLootPayload = {
  mats: number;
  shield: number;
  weapon?: ChestWeaponDrop;
  consumable?: ChestConsumable;
  mods?: WeaponMod[];
  tier: ChestTier;
  /** Rare chest only: +1 air strike (inventory cap enforced in game). */
  airstrike?: boolean;
  rebootCard?: boolean;
  chugSplash?: boolean;
  shockwave?: boolean;
};

export type ChestInteractResult = {
  loot: ChestLootPayload;
  x: number;
  z: number;
};

export type LootLookTarget = { id: string; distance: number; tier: ChestTier };

type OpenParticle = {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
};

type ChestOpenAnim = {
  chest: THREE.Group;
  lid: THREE.Mesh;
  base: THREE.Mesh;
  beam: THREE.Mesh | null;
  t: number;
  baseSwapped: boolean;
  particles: OpenParticle[];
};

type ChestAnticipationVisual = {
  chest: THREE.Group;
  root: THREE.Group;
  ring: THREE.Mesh;
  glow: THREE.Mesh;
  prompt: THREE.Mesh;
  ringMat: THREE.MeshBasicMaterial;
  glowMat: THREE.MeshBasicMaterial;
  promptMat: THREE.MeshBasicMaterial;
  phase: number;
  rare: boolean;
};

type ChestAuraLight = {
  chest: THREE.Group;
  light: THREE.PointLight;
  tier: ChestTier;
  hotZone: boolean;
  phase: number;
};

export class FpsLoot {
  private readonly chests: THREE.Group[] = [];
  private readonly chestTiers = new Map<string, ChestTier>();
  private readonly opened = new Set<string>();
  private readonly openAnims: ChestOpenAnim[] = [];
  private readonly toonGrad: THREE.DataTexture;
  private readonly matChestClosed: THREE.MeshToonMaterial;
  private readonly matChestOpened: THREE.MeshToonMaterial;
  private readonly matLidNormal: THREE.MeshToonMaterial;
  private readonly matLidRare: THREE.MeshToonMaterial;
  private readonly lastPlayer = new THREE.Vector3();
  private readonly lastDir = new THREE.Vector3(0, 0, -1);
  private readonly beamMaterials: THREE.MeshBasicMaterial[] = [];
  private readonly anticipationVisuals: ChestAnticipationVisual[] = [];
  private readonly chestAuraLights: ChestAuraLight[] = [];
  private hotZone: { x: number; z: number; radius: number; multiplier: number } | null = null;

  private static auraColorFor(tier: ChestTier, hotZone: boolean): number {
    if (hotZone) return AURA_GOLD;
    if (tier === 'rare') return AURA_PURPLE;
    return AURA_BLUE;
  }

  private static auraIntensityFor(tier: ChestTier, hotZone: boolean): number {
    if (hotZone) return 1.05;
    if (tier === 'rare') return 0.92;
    return 0.62;
  }

  constructor(
    _scene: THREE.Scene,
    _world: CANNON.World,
    private readonly sceneRoot: THREE.Group,
    positions: { x: number; z: number }[],
  ) {
    this.toonGrad = createToonGradientMap();
    this.matChestClosed = toonMat(0x8b6914, this.toonGrad, { emissive: 0x221105, emissiveIntensity: 0.12 });
    this.matChestOpened = toonMat(0x3d2910, this.toonGrad, { emissive: 0x110805, emissiveIntensity: 0.08 });
    this.matLidNormal = toonMat(0xd4a017, this.toonGrad, { emissive: 0x332006, emissiveIntensity: 0.18 });
    this.matLidRare = toonMat(0x9b59b6, this.toonGrad, { emissive: 0x6a2d8a, emissiveIntensity: 0.42 });

    let i = 0;
    for (const p of positions) {
      const lootId = `loot-${i++}`;
      const tier: ChestTier = Math.random() < RARE_CHEST_CHANCE ? 'rare' : 'normal';
      this.chestTiers.set(lootId, tier);

      const chest = new THREE.Group();
      chest.userData.lootId = lootId;
      chest.userData.tier = tier;

      const base = new THREE.Mesh(new THREE.BoxGeometry(0.9, 0.55, 0.55), this.matChestClosed.clone());
      base.position.y = 0.275;
      base.castShadow = true;
      base.receiveShadow = true;
      base.userData.part = 'base';

      const lidMat = tier === 'rare' ? this.matLidRare.clone() : this.matLidNormal.clone();
      const lid = new THREE.Mesh(new THREE.BoxGeometry(0.92, 0.12, 0.56), lidMat);
      lid.position.set(0, 0.55 + 0.06, -0.08);
      lid.castShadow = true;
      lid.receiveShadow = true;
      lid.userData.part = 'lid';

      const inHotZone = this.isInHotZone(p.x, p.z);
      const beamColor = inHotZone ? BEAM_GOLD : tier === 'rare' ? BEAM_PURPLE : BEAM_GOLD;
      const beam = this.createLootBeam(beamColor);
      const anticipation = this.createAnticipationGlow(chest, beamColor, tier === 'rare' || inHotZone, i * 0.71);
      const aura = this.createChestAuraLight(chest, tier, inHotZone, i * 0.83);
      chest.add(base, lid, beam, anticipation.root, aura.light);
      this.anticipationVisuals.push(anticipation);
      this.chestAuraLights.push(aura);

      chest.position.set(p.x, 0, p.z);
      this.sceneRoot.add(chest);
      this.chests.push(chest);
    }
  }

  private createChestAuraLight(
    chest: THREE.Group,
    tier: ChestTier,
    hotZone: boolean,
    phase: number,
  ): ChestAuraLight {
    const color = FpsLoot.auraColorFor(tier, hotZone);
    const light = new THREE.PointLight(color, FpsLoot.auraIntensityFor(tier, hotZone), 5.8, 1.85);
    light.position.set(0, 0.72, 0);
    light.castShadow = false;
    light.userData.part = 'auraLight';
    return { chest, light, tier, hotZone, phase };
  }

  private refreshChestAura(aura: ChestAuraLight): void {
    const color = FpsLoot.auraColorFor(aura.tier, aura.hotZone);
    aura.light.color.setHex(color);
    aura.light.intensity = FpsLoot.auraIntensityFor(aura.tier, aura.hotZone);
  }

  private createLootBeam(color: number): THREE.Mesh {
    const geo = new THREE.CylinderGeometry(BEAM_RADIUS, BEAM_RADIUS, BEAM_HEIGHT, 14);
    const mat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: BEAM_OPACITY_BASE,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    this.beamMaterials.push(mat);
    const beam = new THREE.Mesh(geo, mat);
    beam.position.y = 0.55 + BEAM_HEIGHT * 0.5;
    beam.userData.part = 'beam';
    return beam;
  }

  private createAnticipationGlow(
    chest: THREE.Group,
    color: number,
    rare: boolean,
    phase: number,
  ): ChestAnticipationVisual {
    const root = new THREE.Group();
    root.userData.part = 'anticipation';

    const ringMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: ANTICIPATION_RING_OPACITY,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    const ring = new THREE.Mesh(new THREE.RingGeometry(0.62, rare ? 0.88 : 0.8, 42), ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.035;
    ring.userData.part = 'anticipation';

    const glowMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: ANTICIPATION_GLOW_OPACITY,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });
    const glow = new THREE.Mesh(new THREE.CircleGeometry(rare ? 0.92 : 0.78, 42), glowMat);
    glow.rotation.x = -Math.PI / 2;
    glow.position.y = 0.025;
    glow.userData.part = 'anticipation';

    const promptMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: ANTICIPATION_PROMPT_OPACITY,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    const prompt = new THREE.Mesh(new THREE.OctahedronGeometry(rare ? 0.12 : 0.09, 0), promptMat);
    prompt.position.y = rare ? 1.06 : 0.96;
    prompt.userData.part = 'anticipation';

    root.add(glow, ring, prompt);
    return { chest, root, ring, glow, prompt, ringMat, glowMat, promptMat, phase, rare };
  }

  /** Pulse loot beams and advance chest open animations. */
  setHotZone(x: number, z: number, radius: number, multiplier: number): void {
    this.hotZone = { x, z, radius, multiplier };
  }

  clearHotZone(): void {
    this.hotZone = null;
  }

  getHotZone(): { x: number; z: number; radius: number } | null {
    if (!this.hotZone) return null;
    return { x: this.hotZone.x, z: this.hotZone.z, radius: this.hotZone.radius };
  }

  isInHotZone(x: number, z: number): boolean {
    if (!this.hotZone) return false;
    return Math.hypot(x - this.hotZone.x, z - this.hotZone.z) <= this.hotZone.radius;
  }

  /** Upgrade unopened chest tiers inside the active hot zone. */
  markHotZoneChests(): void {
    if (!this.hotZone) return;
    for (const chest of this.chests) {
      const id = chest.userData.lootId as string;
      if (this.opened.has(id)) continue;
      if (!this.isInHotZone(chest.position.x, chest.position.z)) continue;
      const tier = this.chestTiers.get(id);
      if (tier !== 'normal') continue;
      if (Math.random() >= 0.62) continue;
      this.chestTiers.set(id, 'rare');
      chest.traverse((obj) => {
        if (!(obj instanceof THREE.Mesh) || obj.userData.part !== 'lid') return;
        obj.material = this.matLidRare.clone();
      });
      const aura = this.chestAuraLights.find((a) => a.chest === chest);
      if (aura) {
        aura.tier = 'rare';
        aura.hotZone = true;
        this.refreshChestAura(aura);
      }
    }
  }

  update(dt: number): void {
    const t = performance.now() * 0.001;
    const opacity = BEAM_OPACITY_BASE + Math.sin(t * BEAM_PULSE_SPEED) * BEAM_PULSE_AMP;
    for (const mat of this.beamMaterials) {
      if (mat.opacity > 0) mat.opacity = opacity;
    }

    for (const aura of this.chestAuraLights) {
      const id = aura.chest.userData.lootId as string;
      if (this.opened.has(id)) {
        aura.light.intensity = 0;
        continue;
      }
      const pulse = (Math.sin(t * AURA_PULSE_SPEED + aura.phase) + 1) * 0.5;
      const base = FpsLoot.auraIntensityFor(aura.tier, aura.hotZone);
      aura.light.intensity = base + pulse * AURA_PULSE_AMP * (aura.hotZone ? 0.34 : aura.tier === 'rare' ? 0.28 : 0.2);
    }

    for (const visual of this.anticipationVisuals) {
      if (!visual.root.visible) continue;
      const pulse = (Math.sin(t * (visual.rare ? 3.25 : 2.65) + visual.phase) + 1) * 0.5;
      visual.ringMat.opacity = ANTICIPATION_RING_OPACITY + pulse * (visual.rare ? 0.18 : 0.1);
      visual.glowMat.opacity = ANTICIPATION_GLOW_OPACITY + pulse * (visual.rare ? 0.1 : 0.06);
      visual.promptMat.opacity = ANTICIPATION_PROMPT_OPACITY + pulse * (visual.rare ? 0.32 : 0.2);
      visual.ring.scale.setScalar(0.96 + pulse * (visual.rare ? 0.16 : 0.1));
      visual.glow.scale.setScalar(0.92 + pulse * 0.13);
      visual.prompt.position.y = (visual.rare ? 1.06 : 0.96) + pulse * 0.1;
      visual.prompt.rotation.y += dt * (visual.rare ? 2.4 : 1.7);
    }

    for (let i = this.openAnims.length - 1; i >= 0; i--) {
      const anim = this.openAnims[i]!;
      anim.t += dt;
      const u = Math.min(1, anim.t / OPEN_ANIM_DURATION);
      const ease = 1 - (1 - u) ** 3;
      anim.lid.rotation.x = LID_OPEN_ANGLE * ease;

      if (!anim.baseSwapped && u >= 0.55) {
        anim.baseSwapped = true;
        (anim.base.material as THREE.Material).dispose();
        anim.base.material = this.matChestOpened.clone();
      }

      if (anim.t >= OPEN_ANIM_DURATION) {
        anim.lid.visible = false;
        if (anim.beam) {
          anim.beam.visible = false;
          const mat = anim.beam.material as THREE.MeshBasicMaterial;
          mat.opacity = 0;
        }
      }

      for (let p = anim.particles.length - 1; p >= 0; p--) {
        const part = anim.particles[p]!;
        part.life -= dt;
        part.mesh.position.addScaledVector(part.vel, dt);
        part.vel.y -= 5.5 * dt;
        const alpha = Math.max(0, part.life / part.maxLife);
        (part.mesh.material as THREE.MeshBasicMaterial).opacity = alpha;
        part.mesh.scale.setScalar(0.65 + alpha * 0.55);
        if (part.life <= 0) {
          part.mesh.removeFromParent();
          part.mesh.geometry.dispose();
          (part.mesh.material as THREE.Material).dispose();
          anim.particles.splice(p, 1);
        }
      }

      if (anim.t >= OPEN_ANIM_DURATION + 0.55 && anim.particles.length === 0) {
        this.openAnims.splice(i, 1);
      }
    }
  }

  /** Call each frame (or before getLookTarget) with the player camera ray. */
  updateLookRay(playerPos: THREE.Vector3, cameraDir: THREE.Vector3): void {
    this.lastPlayer.copy(playerPos);
    this.lastDir.copy(cameraDir).normalize();
  }

  private getChestFocalWorld(chest: THREE.Group): THREE.Vector3 {
    const p = new THREE.Vector3(0, 0.35, 0);
    chest.localToWorld(p);
    return p;
  }

  private pickBestChest(): { id: string; distance: number; dot: number; tier: ChestTier } | null {
    let best: { id: string; distance: number; dot: number; tier: ChestTier } | null = null;

    for (const chest of this.chests) {
      const id = chest.userData.lootId as string;
      if (this.opened.has(id)) continue;

      const pos = this.getChestFocalWorld(chest);
      const toChest = pos.clone().sub(this.lastPlayer);
      const dist = toChest.length();
      if (dist > INTERACT_RANGE || dist < 1e-4) continue;

      const dir = toChest.multiplyScalar(1 / dist);
      const dot = this.lastDir.dot(dir);
      if (dot < LOOK_DOT_MIN) continue;

      const tier = this.chestTiers.get(id) ?? 'normal';
      if (!best || dot > best.dot + 1e-4 || (Math.abs(dot - best.dot) <= 1e-4 && dist < best.distance)) {
        best = { id, distance: dist, dot, tier };
      }
    }
    return best;
  }

  getLookTarget(): LootLookTarget | null {
    const b = this.pickBestChest();
    if (!b) return null;
    return { id: b.id, distance: b.distance, tier: b.tier };
  }

  /** World positions of all chests (for bot pathing). */
  getChestPositions(): { id: string; x: number; z: number }[] {
    return this.chests.map((chest) => ({
      id: chest.userData.lootId as string,
      x: chest.position.x,
      z: chest.position.z,
    }));
  }

  getUnopenedChestPositions(): { id: string; x: number; z: number }[] {
    return this.chests
      .filter((c) => !this.opened.has(c.userData.lootId as string))
      .map((chest) => ({
        id: chest.userData.lootId as string,
        x: chest.position.x,
        z: chest.position.z,
      }));
  }

  isOpened(id: string): boolean {
    return this.opened.has(id);
  }

  /** Scripted open (bots) — visual only, no player loot roll. */
  openChestById(id: string): boolean {
    if (this.opened.has(id)) return false;
    this.opened.add(id);
    const chest = this.chests.find((c) => (c.userData.lootId as string) === id);
    if (chest) this.beginOpenAnimation(chest);
    return true;
  }

  private rollLoot(tier: ChestTier, inHotZone = false): ChestLootPayload {
    let effectiveTier: ChestTier = tier;
    if (inHotZone && tier === 'normal' && Math.random() < 0.35) {
      effectiveTier = 'rare';
    }
    const rare = effectiveTier === 'rare';
    const hotMult = inHotZone && this.hotZone ? this.hotZone.multiplier : 1;
    const mats = Math.floor(
      (rare
        ? 38 + Math.floor(Math.random() * 28)
        : 22 + Math.floor(Math.random() * 18)) * hotMult,
    );
    const shield = Math.floor(
      (rare
        ? 22 + Math.floor(Math.random() * 28)
        : 12 + Math.floor(Math.random() * 18)) * hotMult,
    );

    let weapon: ChestWeaponDrop | undefined;
    let consumable: ChestConsumable | undefined;

    const wRoll = Math.random();
    let weaponKind: WeaponKind | undefined;
    if (rare) {
      if (wRoll < 0.22) weaponKind = 'sniper';
      else if (wRoll < 0.42) weaponKind = 'shotgun';
      else if (wRoll < 0.58) weaponKind = 'smg';
      else if (wRoll < 0.7) weaponKind = 'pistol';
    } else {
      if (wRoll < 0.1) weaponKind = 'smg';
      else if (wRoll < 0.18) weaponKind = 'pistol';
    }
    if (weaponKind) {
      weapon = { kind: weaponKind, rarity: rollRarity() };
    }

    const cRoll = Math.random();
    if (rare) {
      if (cRoll < 0.18) consumable = 'chug';
      else if (cRoll < 0.36) consumable = 'mini';
      else if (cRoll < 0.5) consumable = 'bandage';
    } else {
      if (cRoll < 0.22) consumable = 'bandage';
      else if (cRoll < 0.34) consumable = 'mini';
    }

    const mods = rollModsFromChest();
    const airstrike = rare && Math.random() < 0.15;
    const rebootCard = rare && Math.random() < 0.08;
    const chugSplash = rare && Math.random() < 0.12;
    const shockwave = rare && Math.random() < 0.1;
    return {
      mats,
      shield,
      weapon,
      consumable,
      mods: mods.length > 0 ? mods : undefined,
      tier: effectiveTier,
      airstrike: airstrike || undefined,
      rebootCard: rebootCard || undefined,
      chugSplash: chugSplash || undefined,
      shockwave: shockwave || undefined,
    };
  }

  tryInteract(playerPos: THREE.Vector3, cameraDir: THREE.Vector3): ChestInteractResult | null {
    this.updateLookRay(playerPos, cameraDir);
    const pick = this.pickBestChest();
    if (!pick || this.opened.has(pick.id)) return null;

    this.opened.add(pick.id);
    const chest = this.chests.find((c) => (c.userData.lootId as string) === pick.id);
    if (chest) this.beginOpenAnimation(chest);
    const cx = chest?.position.x ?? playerPos.x;
    const cz = chest?.position.z ?? playerPos.z;
    return {
      loot: this.rollLoot(pick.tier, this.isInHotZone(cx, cz)),
      x: cx,
      z: cz,
    };
  }

  private beginOpenAnimation(chest: THREE.Group): void {
    let lid: THREE.Mesh | null = null;
    let base: THREE.Mesh | null = null;
    let beam: THREE.Mesh | null = null;
    chest.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      if (obj.userData.part === 'lid') lid = obj;
      if (obj.userData.part === 'base') base = obj;
      if (obj.userData.part === 'beam') beam = obj;
    });
    if (!lid || !base) return;

    const lidMesh = lid as THREE.Mesh;
    lidMesh.rotation.x = 0;
    lidMesh.visible = true;

    const particles: OpenParticle[] = [];
    const anticipation = this.anticipationVisuals.find((v) => v.chest === chest);
    if (anticipation) {
      anticipation.root.visible = false;
      anticipation.ringMat.opacity = 0;
      anticipation.glowMat.opacity = 0;
      anticipation.promptMat.opacity = 0;
    }
    const aura = this.chestAuraLights.find((a) => a.chest === chest);
    if (aura) aura.light.intensity = 0;
    const tier = (chest.userData.tier as ChestTier) ?? 'normal';
    const rare = tier === 'rare';
    const sparkleColors = rare
      ? [0xffffff, 0xffeeaa, 0xffd700, 0xddaaff, 0xffccff]
      : [0xffffff, 0xffee88, 0xffd54a, 0xffcc44];
    const count = rare ? 16 + Math.floor(Math.random() * 7) : 10 + Math.floor(Math.random() * 6);
    const origin = new THREE.Vector3();
    chest.getWorldPosition(origin);
    origin.y += 0.72;

    for (let i = 0; i < count; i++) {
      const mesh = new THREE.Mesh(
        new THREE.OctahedronGeometry(0.05 + Math.random() * (rare ? 0.07 : 0.05), 0),
        new THREE.MeshBasicMaterial({
          color: sparkleColors[i % sparkleColors.length]!,
          transparent: true,
          opacity: 1,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }),
      );
      mesh.position.copy(origin);
      mesh.position.x += (Math.random() - 0.5) * (rare ? 0.48 : 0.35);
      mesh.position.z += (Math.random() - 0.5) * (rare ? 0.48 : 0.35);
      const spread = rare ? 3.4 : 2.8;
      const vel = new THREE.Vector3(
        (Math.random() - 0.5) * spread,
        2.6 + Math.random() * (rare ? 3.2 : 2.6),
        (Math.random() - 0.5) * spread,
      );
      const maxLife = (rare ? 0.55 : 0.45) + Math.random() * 0.35;
      this.sceneRoot.add(mesh);
      particles.push({ mesh, vel, life: maxLife, maxLife });
    }

    this.openAnims.push({
      chest,
      lid: lidMesh,
      base: base as THREE.Mesh,
      beam,
      t: 0,
      baseSwapped: false,
      particles,
    });
  }
}
